import { ChangeDetectionStrategy, Component, inject, signal, ViewEncapsulation } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams, IRowNode } from 'ag-grid-enterprise';
import { MetadataEntityData, ResolvedViewGroupHeader, resolveViewGroupHeader } from '@jms/model';
import { ButtonIconComponent } from '@workbuddy/ui/v2/components/button';
import { Command } from '@workbuddy/ui/model';
import { GroupHeaderComponent } from '../components/group.header';
import { resolveListGroupHeaderActions } from '../context';
import { EntityBoardStore } from '../state';

/** AG Grid parameters supplied to the entity-board group-row renderer. */
export type BoardGroupRowParams = ICellRendererParams;

/**
 * AG Grid group-row inner renderer for entity.board.
 *
 * The header phrase is the shared `board-group-header`, the same component the kanban swimlane
 * renders — a Job group row and a Job swimlane must not drift. What is the table's alone stays here:
 * the group's own actions and the click that expands the node.
 */
@Component({
  selector: 'board-group-row',
  standalone: true,
  imports: [GroupHeaderComponent, ButtonIconComponent],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./board.group.row.renderer.less'],
  template: `
    <div class="board-group-row" (click)="onGroupClick($event)">
      <board-group-header [header]="$header()" [record]="$leaf()" [metadata]="$metadata()" [count]="$count()" [countLabel]="$countLabel()" [groupName]="$groupName()" />
      @if ($actions().length) {
        <span class="board-group-row-actions" (click)="$event.stopPropagation()">
          @for (action of $actions(); track action._id) {
            <wb-button-icon [command]="action" displayStyle="Tertiary" size="Small" />
          }
        </span>
      }
    </div>
  `,
})
export class BoardGroupRowRenderer implements ICellRendererAngularComp {

  private readonly store = inject(EntityBoardStore);

  protected readonly $leaf = signal<any>(null);
  protected readonly $metadata = signal<MetadataEntityData | null>(null);
  protected readonly $header = signal<ResolvedViewGroupHeader | null>(null);
  /** AG's own group key — the shared header falls back to it when the leaf stores nothing at the title path. */
  protected readonly $groupName = signal('');
  protected readonly $count = signal(0);
  protected readonly $countLabel = signal('');
  protected readonly $actions = signal<readonly Command[]>([]);
  private node: IRowNode | null = null;

  public agInit(params: BoardGroupRowParams): void {

    this.apply(params);
  }

  public refresh(params: BoardGroupRowParams): boolean {

    this.apply(params);
    return true;
  }

  private apply(params: BoardGroupRowParams): void {

    this.node = params.node;
    const leaf = sampleLeafData(params);
    this.$leaf.set(leaf);

    const view = this.store.$effectiveView();
    const metadata = this.store.$metadata();
    const groupBy = String(params.node?.field ?? view?.table?.groupBy ?? view?.list?.groupBy ?? '') || null;
    const count = params.node?.allChildrenCount ?? params.node?.allLeafChildren?.length ?? 0;
    this.$actions.set(resolveListGroupHeaderActions(this.store.$context()?.list, {
      key: params.node?.key == null ? null : String(params.node.key),
      fieldId: groupBy,
      value: groupBy ? getValueFromPath(leaf, groupBy) : params.node?.key,
      item: leaf,
      count,
    }));

    // metadata.groupHeaderDefaults on the groupBy field supply title/elevated fields when the view
    // has no override.
    this.$metadata.set(metadata ?? null);
    this.$header.set(metadata ? resolveViewGroupHeader(view, metadata, groupBy) : null);
    this.$groupName.set(params.node?.key == null ? '' : String(params.node.key));
    this.$count.set(count);
    // Linear: the count sits next to the title as a bare number, not a "1 stage" pill. The kanban
    // words its own count differently, which is why the phrase is the host's and not the header's.
    this.$countLabel.set(String(count));
  }

  protected onGroupClick(event: MouseEvent): void {

    if (!this.node?.group) return;
    event.stopPropagation();
    this.node.setExpanded(!this.node.expanded);
  }
}

function sampleLeafData(params: BoardGroupRowParams): any {

  const leaves = params.node?.allLeafChildren;
  if (leaves?.length) return leaves[0].data ?? null;

  // Walk first child if allLeafChildren not populated yet
  let node: any = params.node;
  while (node?.group && node.childrenAfterGroup?.length) {
    node = node.childrenAfterGroup[0];
  }
  return node?.data ?? null;
}

function getValueFromPath(item: unknown, path: string): unknown {

  if (!item || !path) return null;
  return path.split('.').reduce((value: any, part) => value?.[part], item as any);
}
